/**
 * API client for the SIGB backend.
 * Handles authentication, token management, and API requests.
 */

import type { ApiError, LoginResponse, CurrentUser } from "./types";

// Use || instead of ?? so empty string falls back to "/api"
const API_BASE_URL: string = import.meta.env.VITE_API_URL || "/api";
const TOKEN_KEY = "sigb_jwt_token";

/**
 * Get the stored JWT token.
 */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Store the JWT token.
 */
export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * Remove the stored JWT token.
 */
export function removeToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Check if user is authenticated (has a token).
 */
export function isAuthenticated(): boolean {
  return getToken() !== null;
}

/**
 * API error class with structured error information.
 */
export class ApiClientError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: ApiError
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

/**
 * Make an API request with automatic token handling.
 */
export async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  // Merge additional headers if provided (only supports Record-style headers)
  if (options.headers && typeof options.headers === "object" && !Array.isArray(options.headers)) {
    const additionalHeaders = options.headers as Record<string, string>;
    for (const [key, value] of Object.entries(additionalHeaders)) {
      headers[key] = value;
    }
  }

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const url = endpoint.startsWith("http") ? endpoint : `${API_BASE_URL}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Handle 401 - Token expired or invalid
  if (response.status === 401) {
    removeToken();
    // Dispatch custom event for auth context to handle
    window.dispatchEvent(new CustomEvent("auth:unauthorized"));
  }

  if (!response.ok) {
    let errorData: ApiError | undefined;
    try {
      errorData = (await response.json()) as ApiError;
    } catch {
      // Response body is not JSON
    }

    throw new ApiClientError(
      errorData?.message ??
        errorData?.error?.message ??
        errorData?.detail ??
        `Request failed with status ${String(response.status)}`,
      response.status,
      errorData
    );
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

/**
 * GET request helper.
 */
export function get<T>(endpoint: string): Promise<T> {
  return apiRequest<T>(endpoint, { method: "GET" });
}

/**
 * POST request helper.
 */
export function post<T>(endpoint: string, data?: unknown): Promise<T> {
  const options: RequestInit = { method: "POST" };
  if (data !== undefined) {
    options.body = JSON.stringify(data);
  }
  return apiRequest<T>(endpoint, options);
}

/**
 * PATCH request helper.
 *
 * Sent as a JSON merge patch: API Platform rejects PATCH requests in any other
 * format (415), and the custom controllers read the raw JSON body either way.
 */
export function patch<T>(endpoint: string, data?: unknown): Promise<T> {
  const options: RequestInit = {
    method: "PATCH",
    headers: { "Content-Type": "application/merge-patch+json" },
  };
  if (data !== undefined) {
    options.body = JSON.stringify(data);
  }
  return apiRequest<T>(endpoint, options);
}

/**
 * PUT request helper.
 */
export function put<T>(endpoint: string, data?: unknown): Promise<T> {
  const options: RequestInit = { method: "PUT" };
  if (data !== undefined) {
    options.body = JSON.stringify(data);
  }
  return apiRequest<T>(endpoint, options);
}

/**
 * DELETE request helper.
 */
export function del<T = void>(endpoint: string): Promise<T> {
  return apiRequest<T>(endpoint, { method: "DELETE" });
}

/**
 * Login and store the JWT token.
 */
export async function login(email: string, password: string): Promise<LoginResponse> {
  const response = await post<LoginResponse>("/login", {
    username: email,
    password,
  });

  setToken(response.token);

  return response;
}

/**
 * Logout and clear the stored token.
 */
export function logout(): void {
  removeToken();
  window.dispatchEvent(new CustomEvent("auth:logout"));
}

/**
 * Get the current user's information.
 */
export function getCurrentUser(): Promise<CurrentUser> {
  return get<CurrentUser>("/me");
}

/**
 * Upload a file using FormData.
 */
export async function uploadFile<T>(
  endpoint: string,
  file: File,
  additionalData?: Record<string, string>
): Promise<T> {
  const token = getToken();
  const formData = new FormData();
  formData.append("file", file);

  if (additionalData) {
    for (const [key, value] of Object.entries(additionalData)) {
      formData.append(key, value);
    }
  }

  const url = endpoint.startsWith("http") ? endpoint : `${API_BASE_URL}${endpoint}`;

  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: formData,
  });

  if (response.status === 401) {
    removeToken();
    window.dispatchEvent(new CustomEvent("auth:unauthorized"));
  }

  if (!response.ok) {
    let errorData: ApiError | undefined;
    try {
      errorData = (await response.json()) as ApiError;
    } catch {
      // Response body is not JSON
    }

    throw new ApiClientError(
      errorData?.message ??
        errorData?.error?.message ??
        errorData?.detail ??
        `Upload failed with status ${String(response.status)}`,
      response.status,
      errorData
    );
  }

  return response.json() as Promise<T>;
}
